import { css, cx } from '@emotion/css';
import type { ReactNode, HTMLAttributes } from 'react';
import { FiX } from 'react-icons/fi';

const overlay = css({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
  padding: 'var(--spacing-md)',
});

const modalContainer = css({
  backgroundColor: 'var(--primary-color)',
  borderRadius: 'var(--spacing-sm)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  width: 'clamp(320px, 90vw, 600px)',
  maxHeight: '90vh',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  '@media (max-width: 520px)': {
    width: 'clamp(280px, 95vw, 600px)',
  },
});

const header = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: 'var(--spacing-lg) var(--spacing-xl)',
  borderBottom: '1px solid var(--g-200)',
  '@media (max-width: 480px)': {
    padding: 'var(--spacing-md) var(--spacing-lg)',
  },
});

const titleStyle = css({
  fontSize: 'var(--fs-450)',
  fontWeight: 600,
  color: 'var(--g-800)',
  margin: 0,
  '@media (max-width: 480px)': {
    fontSize: 'var(--fs-400)',
  },
});

const closeButton = css({
  backgroundColor: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: 'var(--g-500)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'var(--spacing-xs)',
  borderRadius: '50%',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'var(--g-100)',
    color: 'var(--g-800)',
  },
});

const content = css({
  padding: 'var(--spacing-xl)',
  overflowY: 'auto',
  flex: 1,
  '@media (max-width: 480px)': {
    padding: 'var(--spacing-lg)',
  },
});

export interface ModalProps extends HTMLAttributes<HTMLDivElement> {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
}

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  className,
  ...props
}: ModalProps) {
  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className={overlay} onClick={handleOverlayClick}>
      <div className={cx(modalContainer, className)} {...props}>
        {title && (
          <div className={header}>
            <h3 className={titleStyle}>{title}</h3>
            <button
              className={closeButton}
              onClick={onClose}
              aria-label="Fechar"
              type="button"
            >
              <FiX size={20} />
            </button>
          </div>
        )}
        <div className={content}>{children}</div>
      </div>
    </div>
  );
}

export default Modal;
